import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { StarRating } from '../components/StarRating';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  MapPin,
  Phone,
  Globe,
  Mail,
  Clock,
  Star,
  ArrowLeft,
  Share2,
  Heart,
  Pencil,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryImages = {
  restaurants: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=80',
  retail: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=1200&q=80',
  services: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&q=80',
  professional: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200&q=80',
  beauty: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&q=80',
  churches: 'https://images.unsplash.com/photo-1438032005730-c779502df39b?w=1200&q=80',
  nonprofits: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=1200&q=80',
  foundations: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=1200&q=80',
  charities: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=1200&q=80',
  organizations: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=1200&q=80',
  photography: 'https://images.unsplash.com/photo-1542038784456-1ea8e935640e?w=1200&q=80',
  videography: 'https://images.unsplash.com/photo-1579566346927-c68383817a25?w=1200&q=80',
  graphicdesign: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=1200&q=80',
  museums: 'https://images.unsplash.com/photo-1554907984-15263bfd63bd?w=1200&q=80',
  filmproduction: 'https://images.unsplash.com/photo-1485846234645-a62644f84728?w=1200&q=80',
  recordingcompany: 'https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=1200&q=80',
  adventures: 'https://images.unsplash.com/photo-1527631746610-bca00a040d60?w=1200&q=80',
  cruises: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=1200&q=80',
  vacationrentals: 'https://images.unsplash.com/photo-1499793983690-e29da59ef1c2?w=1200&q=80',
  travelagencies: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200&q=80',
};

const getCategoryImage = (category) => {
  const key = category?.toLowerCase().replace(/[^a-z]/g, '').replace('wellness', '');
  return categoryImages[key] || categoryImages.services;
};

const BusinessDetailPage = () => {
  const { businessId } = useParams();
  const { isAuthenticated, user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchBusiness = async () => {
    try {
      const [businessRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/businesses/${businessId}`),
        axios.get(`${API}/businesses/${businessId}/reviews`)
      ]);
      setBusiness(businessRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch business:', error);
      }
      toast.error('Business not found');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusiness();
  }, [businessId]);

  const handleSubmitReview = async () => {
    if (newRating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (!newComment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmitting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/businesses/${businessId}/reviews`,
        { rating: newRating, comment: newComment },
        { headers, withCredentials: true }
      );
      toast.success('Review submitted successfully!');
      setReviewDialogOpen(false);
      setNewRating(0);
      setNewComment('');
      fetchBusiness();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('You have already reviewed this business');
      } else {
        toast.error('Failed to submit review');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: business.name,
        text: business.description,
        url: window.location.href,
      });
    } catch (error) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-white">
        <Navbar />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-deep border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-stone-white">
        <Navbar />
        <div className="pt-24 text-center py-16">
          <h1 className="font-display text-2xl font-bold text-stone-900">Business Not Found</h1>
          <Link to="/directory">
            <Button className="mt-4">Back to Directory</Button>
          </Link>
        </div>
      </div>
    );
  }

  const imageUrl = business.image_url || getCategoryImage(business.category);
  const isOwner = user?.user_id === business.owner_id;

  return (
    <div className="min-h-screen bg-stone-white" data-testid="business-detail-page">
      <Navbar />

      {/* Hero Image */}
      <div className="relative h-64 md:h-96 mt-16">
        <img
          src={imageUrl}
          alt={business.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Back Button */}
        <Link
          to="/directory"
          className="absolute top-4 left-4 flex items-center gap-2 bg-white/90 backdrop-blur px-4 py-2 rounded-full text-stone-700 hover:bg-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </Link>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex gap-2">
          <button
            onClick={handleShare}
            className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors"
            data-testid="share-btn"
          >
            <Share2 className="w-5 h-5 text-stone-700" />
          </button>
          <button
            className="w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-colors"
            data-testid="save-btn"
          >
            <Heart className="w-5 h-5 text-stone-700" />
          </button>
        </div>

        {/* Category Badge */}
        <div className="absolute bottom-4 left-4">
          <Badge className="bg-emerald-deep text-white px-4 py-1.5">
            {business.category}
          </Badge>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <h1 className="font-display text-3xl md:text-4xl font-bold text-stone-900" data-testid="business-name">
                  {business.name}
                </h1>
                {isOwner && (
                  <Link to={`/edit-business/${business.business_id}`}>
                    <Button variant="outline" className="gap-2" data-testid="edit-btn">
                      <Pencil className="w-4 h-4" />
                      Edit
                    </Button>
                  </Link>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 mt-3">
                <StarRating rating={business.average_rating} />
                <span className="text-lg font-semibold text-stone-900">
                  {business.average_rating.toFixed(1)}
                </span>
                <span className="text-stone-500">
                  ({business.review_count} reviews)
                </span>
              </div>

              {/* Owner */}
              <p className="mt-2 text-stone-500">
                Owned by <span className="text-stone-700 font-medium">{business.owner_name}</span>
              </p>
            </div>

            {/* Description */}
            <div>
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-3">About</h2>
              <p className="text-stone-600 leading-relaxed" data-testid="business-description">
                {business.description}
              </p>
            </div>

            {/* Reviews Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-xl font-semibold text-stone-900">
                  Reviews ({reviews.length})
                </h2>
                {isAuthenticated ? (
                  <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full" data-testid="write-review-btn">
                        Write a Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-display">Write a Review</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <label className="text-sm font-medium text-stone-700 mb-2 block">
                            Your Rating
                          </label>
                          <StarRating
                            rating={newRating}
                            onRatingChange={setNewRating}
                            interactive
                            size="lg"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-stone-700 mb-2 block">
                            Your Review
                          </label>
                          <Textarea
                            placeholder="Share your experience..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            rows={4}
                            data-testid="review-comment-input"
                          />
                        </div>
                        <Button
                          onClick={handleSubmitReview}
                          disabled={submitting}
                          className="w-full bg-emerald-deep hover:bg-emerald-deep/90 text-white"
                          data-testid="submit-review-btn"
                        >
                          {submitting ? 'Submitting...' : 'Submit Review'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Link to="/login">
                    <Button variant="outline" className="rounded-full">
                      Sign in to Review
                    </Button>
                  </Link>
                )}
              </div>

              {/* Reviews List */}
              {reviews.length > 0 ? (
                <div className="space-y-4" data-testid="reviews-list">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="review-card" data-testid={`review-${review.review_id}`}>
                      <div className="flex items-start gap-4">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-emerald-deep/10 text-emerald-deep font-semibold">
                            {review.user_name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-stone-900">{review.user_name}</h4>
                            <span className="text-sm text-stone-500">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 my-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={`review-${review.review_id}-star-${i}`}
                                className={`w-4 h-4 ${
                                  i < review.rating
                                    ? 'fill-legacy-gold text-legacy-gold'
                                    : 'text-stone-300'
                                }`}
                              />
                            ))}
                          </div>
                          <p className="text-stone-600 mt-2">{review.comment}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-stone-50 rounded-xl" data-testid="no-reviews">
                  <Star className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No reviews yet. Be the first to review!</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-white rounded-xl p-6 border border-stone-200 shadow-sm" data-testid="contact-card">
              <h3 className="font-display text-lg font-semibold text-stone-900 mb-4">
                Contact Information
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-emerald-deep mt-0.5" />
                  <div>
                    <p className="text-stone-700">{business.address}</p>
                    <p className="text-stone-700">
                      {business.city}
                      {business.state && `, ${business.state}`}
                      {business.zip_code && ` ${business.zip_code}`}
                    </p>
                    {business.country && business.country !== 'United States' && (
                      <p className="text-stone-600">{business.country}</p>
                    )}
                  </div>
                </div>

                {business.phone && (
                  <a
                    href={`tel:${business.phone}`}
                    className="flex items-center gap-3 text-stone-700 hover:text-emerald-deep transition-colors"
                  >
                    <Phone className="w-5 h-5 text-emerald-deep" />
                    <span>{business.phone}</span>
                  </a>
                )}

                {business.email && (
                  <a
                    href={`mailto:${business.email}`}
                    className="flex items-center gap-3 text-stone-700 hover:text-emerald-deep transition-colors"
                  >
                    <Mail className="w-5 h-5 text-emerald-deep" />
                    <span>{business.email}</span>
                  </a>
                )}

                {business.website && (
                  <a
                    href={business.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-stone-700 hover:text-emerald-deep transition-colors"
                  >
                    <Globe className="w-5 h-5 text-emerald-deep" />
                    <span>Visit Website</span>
                  </a>
                )}
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-stone-100 rounded-xl h-64 flex items-center justify-center" data-testid="map-placeholder">
              <div className="text-center">
                <MapPin className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                <p className="text-stone-500 text-sm">Map coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BusinessDetailPage;
